import { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import DateTimePicker from 'react-datetime';
import moment from 'moment';

import { UserSubscriptionTier } from "../../../../__gqltypes__";
import { applyChangeIfDateValid, validIfSameDayOrAfter } from '../../../../utils/datepicker';

export type EventMatchersConfig = {
  filterSubscriptionTier: UserSubscriptionTier | undefined | null;
  filterUserIds: string | undefined | null;
  filterUserMinCreationDate: Date | undefined | null;
  filterUserMaxCreationDate: Date | undefined | null;
  filterAppVersion: string | undefined | null;
  filterAppVersionOperator?: string | null;
  filterUserLevel: number | undefined | null;
}

// '>=' first: the default, and the behavior of entries saved without an operator.
export const APP_VERSION_OPERATORS = ['>=', '=', '!=', '<=', '>', '<'] as const;

export const eventMatcherDefaultConfig: EventMatchersConfig = {
  filterSubscriptionTier: undefined,
  filterUserIds: '',
  filterUserMinCreationDate: undefined,
  filterUserMaxCreationDate: undefined,
  filterAppVersion: undefined,
  filterUserLevel: undefined,
};

type Props = {
  config: EventMatchersConfig;
  onUpdate: (newConfig: EventMatchersConfig | ((prev: EventMatchersConfig) => EventMatchersConfig)) => void;
  // Opt-in (Marathon): accent the collapsed header and summarize the min app version there.
  highlight?: boolean;
  // Opt-in (Marathon): show the app-version operator select; other events keep the implicit '>='.
  showAppVersionOperator?: boolean;
  // false: always show the fields, no expand / collapse toggle
  collapsible?: boolean;
}

export function userIdsArrayToString(userIdsArray: string[] | string): string {
  if (Array.isArray(userIdsArray)) {
    return userIdsArray.join(', ');
  }
  return userIdsArray;
}

export function userIdsStringToArray(songIdsString: string): string[] | undefined {
  if (typeof songIdsString === 'string' && songIdsString.trim().length > 0) {
    return songIdsString.split(',').map((id) => id.trim());
  }
  return undefined;
}

// One short label per active matcher, so the collapsed header tells who sees the event
export function eventMatchersSummary(config: EventMatchersConfig, showAppVersionOperator = false): string[] {
  const summary: string[] = [];
  if (config.filterAppVersion) {
    summary.push(`App ${showAppVersionOperator ? config.filterAppVersionOperator ?? '>=' : '≥'} ${config.filterAppVersion}`);
  }
  if (config.filterUserLevel) {
    summary.push(`Level ≥ ${config.filterUserLevel}`);
  }
  if (config.filterSubscriptionTier) {
    summary.push(`Tier ${config.filterSubscriptionTier}`);
  }
  if (config.filterUserMinCreationDate) {
    summary.push(`Registered from ${moment(config.filterUserMinCreationDate).format('YYYY-MM-DD')}`);
  }
  if (config.filterUserMaxCreationDate) {
    summary.push(`Registered until ${moment(config.filterUserMaxCreationDate).format('YYYY-MM-DD')}`);
  }
  const userIdCount = userIdsStringToArray(config.filterUserIds ?? '')?.length ?? 0;
  if (userIdCount > 0) {
    summary.push(`${userIdCount} user id${userIdCount > 1 ? 's' : ''} only`);
  }
  return summary;
}

export function EventMatchers(props: Props): JSX.Element {
    const {config, onUpdate, highlight = false, showAppVersionOperator = false, collapsible = true} = props;

    const [expanded, setExpanded] = useState(false);
    const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<UserSubscriptionTier | undefined | 'All'>(
      'All'
    );
    const [filterUserIds, setFilterUserIds] = useState<string>('');
    const [filterUserMinCreationDate, setFilterUserMinCreationDate] = useState<Date | undefined>();
    const [filterUserMaxCreationDate, setFilterUserMaxCreationDate] = useState<Date | undefined>();
    const [filterAppVersion, setFilterAppVersion] = useState<string | undefined>();
    const [filterAppVersionOperator, setFilterAppVersionOperator] = useState<string>('>=');
    const [filterUserLevel, setFilterUserLevel] = useState<number | undefined>();

    const handleChangeAppVersion = useCallback((event) => {
      setFilterAppVersion(event.target.value);
      onUpdate((prev) => ({...prev, filterAppVersion: event.target.value}));
    }, [onUpdate]);

    const handleChangeAppVersionOperator = useCallback((event) => {
      setFilterAppVersionOperator(event.target.value);
      onUpdate((prev) => ({...prev, filterAppVersionOperator: event.target.value}));
    }, [onUpdate]);

    const handleChangeUserLevel = useCallback((event) => {
      // empty field = no level matcher (+'' would give 0)
      const raw = event.target.value.trim();
      const val = raw !== '' && !Number.isNaN(+raw) ? +raw : undefined;
      setFilterUserLevel(val);
      onUpdate((prev) => ({...prev, filterUserLevel: val}));
    }, [onUpdate]);

    const handleChangeSubscriberStatus = useCallback((event) => {
      const val = event.currentTarget.value === 'All' ? undefined : (event.currentTarget.value as UserSubscriptionTier);
      setFilterSubscriptionTier(val);
      onUpdate((prev) => ({...prev, filterSubscriptionTier: val}));
    }, [onUpdate]);

    const handleChangePlayerIds = useCallback((event) => {
      setFilterUserIds(event.target.value);
      onUpdate((prev) => ({...prev,filterUserIds: event.target.value}));
    }, [onUpdate]);

    const handleMinDate = useCallback((value: Date) => {
      setFilterUserMinCreationDate(value);
      onUpdate((prev) => ({...prev, filterUserMinCreationDate: value}));
    }, [onUpdate]);
    
    const handleMaxDate = useCallback((value: Date) => {
      setFilterUserMaxCreationDate(value);
      onUpdate((prev) => ({...prev, filterUserMaxCreationDate: value}));
    }, [onUpdate]);

    useEffect(() => {
      setFilterUserIds(config.filterUserIds ?? '');
      setFilterSubscriptionTier(config.filterSubscriptionTier ?? undefined);
      setFilterUserMinCreationDate((d) => {
        if (!d || d.valueOf() !== config.filterUserMinCreationDate?.valueOf()) {
          return config.filterUserMinCreationDate ?? undefined;
        }
        return d;
      });
      setFilterUserMaxCreationDate((d) => {
        if (!d || d.valueOf() !== config.filterUserMaxCreationDate?.valueOf()) {
          return config.filterUserMaxCreationDate ?? undefined;
        }
        return d;
      });
      setFilterAppVersion(config.filterAppVersion ?? undefined);
      setFilterAppVersionOperator(config.filterAppVersionOperator ?? '>=');
      setFilterUserLevel(config.filterUserLevel ?? undefined);
    }, [config]);

    const summary = eventMatchersSummary(config, showAppVersionOperator);

    const title = (
      <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        Matchers
        {highlight &&
          (summary.length > 0 ? (
            summary.map((label) => (
              <Badge key={label} variant="primary" className="ml-2">
                {label}
              </Badge>
            ))
          ) : (
            <Badge variant="success" className="ml-2">
              All players
            </Badge>
          ))}
      </span>
    );

    const fields = (
          <>
            <Row className="mb-3 align-items-center">
              <Col>
                <small className="text-muted">
                  A player must match <strong>every</strong> filled rule to see the event. Leave a field empty to
                  not filter on it.
                </small>
              </Col>
              <Col md="auto">
              <Button variant="danger" onClick={() => onUpdate(eventMatcherDefaultConfig)}>
                <i className="fas fa-recycle" /> Reset
              </Button>
              </Col>
            </Row>
              
            <Row className="mb-3">
              <Col md={3}>Minimum Registration Date:</Col>
              <Col md={5}>
                <DateTimePicker  
                  dateFormat="YYYY-MM-DD"
                  timeFormat={true}
                  timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                  // @ts-ignore
                  onChange={applyChangeIfDateValid(handleMinDate)}
                  // @ts-ignore
                  value={filterUserMinCreationDate ?? undefined}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>Maximum Registration Date:</Col>
              <Col md={5}>
                <DateTimePicker
                  dateFormat="YYYY-MM-DD"
                  timeFormat={true}
                  timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                  // @ts-ignore
                  onChange={applyChangeIfDateValid(handleMaxDate)}
                  // @ts-ignore
                  value={filterUserMaxCreationDate ?? undefined}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>{showAppVersionOperator ? 'App Version:' : 'Min App Version:'}</Col>
              {showAppVersionOperator && (
                <Col md={2}>
                  <Form.Group>
                    <Form.Control
                      as="select"
                      type="text"
                      value={filterAppVersionOperator}
                      onChange={handleChangeAppVersionOperator}
                    >
                      {APP_VERSION_OPERATORS.map((operator) => (
                        <option key={operator} value={operator}>
                          {operator}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}
              <Col md={showAppVersionOperator ? 3 : 5}>
                <Form.Control
                  type="text"
                  placeholder="Any version, e.g. 3.12.0"
                  value={filterAppVersion ?? ''}
                  onChange={handleChangeAppVersion}
                />
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={3}>Min User Level:</Col>
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Any level"
                  value={filterUserLevel ?? ''}
                  onChange={handleChangeUserLevel}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>User Subscription Status:</Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Control
                    as="select"
                    type="text"
                    value={filterSubscriptionTier}
                    onChange={handleChangeSubscriberStatus}
                  >
                    <option>All</option>
                    {Object.keys(UserSubscriptionTier).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                User Ids: <br />
                (separated by commas &quot;,&quot;)
              </Col>
              <Col md={8}>
                <Form.Control
                  as="textarea"
                  placeholder="Any player. Filled = only these players see the event"
                  value={filterUserIds.toString()}
                  onChange={handleChangePlayerIds}
                />
              </Col>
            </Row>
            </>
    );

    if (!collapsible) {
      return (
        <>
          <Card.Title className={highlight ? 'event-matchers-highlight' : undefined}>{title}</Card.Title>
          {fields}
        </>
      );
    }

    return (
      <Accordion>
        <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(value => !value)}>
            <div
              className={highlight ? 'event-matchers-highlight' : undefined}
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {title}
            <Button variant="link">
              {!expanded && <i className="fas fa-chevron-down" />}
              {expanded && <i className="fas fa-chevron-up" />}
            </Button>
          </div>
        </Accordion.Toggle>
        <Accordion.Collapse eventKey="0">
          {fields}
        </Accordion.Collapse>
      </Accordion>
    );
}